// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import PlanetIcon, { getPlanetColors } from './PlanetIcon';
import StationIcon from './StationIcon';
import WarpGateVisualization from './WarpGateVisualization';
import './ArrivalScreen.css';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import { useUniverse } from '../contexts/UniverseContext';

/**
 * Unified ArrivalScreen component for planets, stations, and warp gates
 * @param {Object} location - The location object (planet, station, or warpgate)
 * @param {Function} onEnterOrbit - Callback when entering orbit/docking
 * @param {string} type - 'planet', 'station', or 'warpgate'
 */
const ArrivalScreen = ({ location, onEnterOrbit, type = 'planet' }) => {
  const [zoomProgress, setZoomProgress] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const { getCurrentUniverseInfo } = useUniverse();

  const isStation = type === 'station';
  const isWarpGate = type === 'warpgate';

  const planetColors = !isStation && !isWarpGate ? getPlanetColors(location) : null;

  const getArrivalBg = () => {
    const universeId = getCurrentUniverseInfo()?.id || 'milkyway';
    const typeMap = { station: 'station', warpgate: 'station', planet: 'terrestrial' };
    const locType = (location?.type || type || 'barren').toLowerCase();
    const filename = typeMap[locType] || locType.replace(' ', '_') || 'barren';
    return `url(/images/planets/${universeId}/${filename}.png), url(/images/planets/${filename}.png)`;
  };

  useEffect(() => {
    // Animate zoom/approach over 3 seconds
    const startTime = Date.now();
    const duration = 3000; // 3 seconds

    const animateZoom = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Apply easing function for smoother approach (ease-out cubic)
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      
      setZoomProgress(easedProgress);

      if (progress < 1) {
        requestAnimationFrame(animateZoom);
      } else {
        // Show button after zoom completes
        setShowButton(true);
      }
    };

    requestAnimationFrame(animateZoom);
  }, []);

  // Calculate scale - use station approach range for all types (better visual)
  const scale = 0.2 + (zoomProgress * 0.6);  // 0.2 to 0.8 for all
  
  // Calculate opacity (fade in quickly)
  const opacity = Math.min(zoomProgress * 2, 1);

  return createPortal(
    <div className="arrival-screen-overlay" style={{ backgroundImage: getArrivalBg() }}>
      <div className="arrival-screen-container">
        {/* Approaching planet or station */}
        <div className="arrival-planet-container">
          <div 
            className="arrival-planet"
            style={{
              transform: `translate(-50%, -50%) scale(${scale})`,
              opacity: opacity
            }}
          >
            {isWarpGate ? (
              <WarpGateVisualization size={400} discovered={location.discovered !== false} />
            ) : isStation ? (
              <StationIcon size={400} color="#00ffff" animated={true} />
            ) : (
              <PlanetIcon 
                size={600} 
                primaryColor={planetColors.primary}
                secondaryColor={planetColors.secondary}
                hasAtmosphere={true}
                hasRings={planetColors.hasRings}
              />
            )}
          </div>

          {/* Star field background */}
          <div className="arrival-stars">
            {[...Array(100)].map((_, i) => (
              <div
                key={i}
                className="arrival-star"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `twinkle 2s ease-in-out infinite ${Math.random() * 2}s`,
                  opacity: Math.random() * 0.8 + 0.2
                }}
              />
            ))}
          </div>
        </div>

        {/* Location info panel */}
        <div className={`arrival-info-panel ${showButton ? 'visible' : ''}`}>
          <div className="arrival-header">
            <div className="arrival-title">APPROACHING</div>
            <div className="arrival-location">{location.name}</div>
          </div>

          <div className="arrival-details">
            <div className="arrival-detail-row">
              <span className="arrival-label">DISTANCE:</span>
              <span className="arrival-value">{location.distance} LY FROM SOL</span>
            </div>
            {isWarpGate ? (
              <>
                <div className="arrival-detail-row">
                  <span className="arrival-label">TYPE:</span>
                  <span className="arrival-value">DIMENSIONAL GATEWAY</span>
                </div>
                <div className="arrival-detail-row">
                  <span className="arrival-label">STATUS:</span>
                  <span className="arrival-value">{location.resources}</span>
                </div>
              </>
            ) : isStation ? (
              <>
                <div className="arrival-detail-row">
                  <span className="arrival-label">TYPE:</span>
                  <span className="arrival-value">DEEP SPACE STATION</span>
                </div>
                <div className="arrival-detail-row">
                  <span className="arrival-label">SERVICES:</span>
                  <span className="arrival-value">{location.resources}</span>
                </div>
              </>
            ) : (
              <div className="arrival-detail-row">
                <span className="arrival-label">RESOURCES:</span>
                <span className="arrival-value">{location.resources}</span>
              </div>
            )}
            <div className="arrival-detail-row">
              <span className="arrival-label">THREAT:</span>
              <span className={`arrival-value threat-${location.threat.toLowerCase()}`}>
                {location.threat}
              </span>
            </div>
          </div>

          {showButton && (
            <Button 
              variant="primary"
              size="large"
              onClick={() => {
                EventBus.emit(EVENT_TYPES.SYSTEM_VISITED, location.id);
                EventBus.emit(EVENT_TYPES.FIRST_ORBIT);
                onEnterOrbit();
              }}
            >
              {isWarpGate ? 'APPROACH GATE →' : (isStation ? 'REQUEST DOCKING →' : 'ENTER ORBIT →')}
            </Button>
          )}

          {!showButton && (
            <div className="arrival-status">
              <div className="status-spinner"></div>
              <span>{isWarpGate ? 'APPROACHING GATEWAY...' : (isStation ? 'REDUCING VELOCITY...' : 'DECELERATING...')}</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ArrivalScreen;
